import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { TFunction } from "react-i18next";
import Axios from "axios";
import type { AxiosError } from "axios";
import useSWR from "swr";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import styled from "styled-components/macro";
import {
  ConfirmOrCancelButtonContainer,
  OutlineIconButtonMedium,
  SecondaryButtonWithPlusIcon,
} from "../../../../../components/Buttons/Buttons";
import { DeleteIcon } from "../../../../../components/Icons/Icons";
import { TextMdBold } from "../../../../../components/Typography/TypographyV2";
import { Form } from "../../../../../layout/FormLayout";
import { SelectWithDeleteButton } from "../../../../../layout/shared";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import { SearchSelectInfiniteScroll } from "../../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { useFormWrapper, useStoreState } from "../../../../../util/util";
import { strings } from "../../../../../util/strings";
import { findNonEmptyDuplicateIndexes } from "../../../../../util/form.utils";
import { endpoints } from "../../../../../endpoints";
import type {
  AdvancedFilterCollectionInstance,
  AdvancedFilterCollectionInstanceInput,
  AdvancedFilterOtherFilterInput,
  AdvancedFilterQuantitativeFilterInput,
  OptionType,
} from "../../../../../types/types";
import type {
  AttributeInput,
  AttributeSchema,
  CollectionSchema,
} from "../../../../../types/types.PIM";
import {
  QuantitativeFilterCard,
  emptyQuantitativeFilterRow,
} from "./QuantitativeFilterCard";
import type {
  QuantitativeFilterFormRow,
  QuantitativeFilterSlot,
} from "./QuantitativeFilterCard";
import { spacingTokens } from "../../../../../designTokens";

const FilterSection = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
  margin-top: ${({ theme }) => theme.resolve("spacing_md")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};

  > * + * {
    margin-top: ${({ theme }) => theme.resolve("spacing_md")};
  }
`;

const KEY_ATTRIBUTE_INPUT_TYPES: AttributeInput[] = [
  "single_select",
  "multi_select",
];
const OTHER_FILTER_INPUT_TYPES: AttributeInput[] = [
  "single_select",
  "multi_select",
];
const QUANTITATIVE_INPUT_TYPES: AttributeInput[] = ["numeric"];

type OtherFilterFormRow = { attribute: OptionType | null };

type FormValues = {
  collection: OptionType | null;
  key_attribute: OptionType | null;
  quantitative_filters: QuantitativeFilterFormRow[];
  other_filters: OtherFilterFormRow[];
};

const zodNullableOption = z
  .object({ value: z.string(), label: z.string() })
  .nullable();

const quantitativeFilterRowSchema = (t: TFunction) =>
  z
    .object({
      kind: z.union([z.literal("single"), z.literal("range")]),
      display_name: z
        .string()
        .optional()
        .transform((value) => value ?? ""),
      is_attribute: zodNullableOption
        .optional()
        .transform((value) => value ?? null),
      min_attribute: zodNullableOption
        .optional()
        .transform((value) => value ?? null),
      max_attribute: zodNullableOption
        .optional()
        .transform((value) => value ?? null),
    })
    .superRefine((row, ctx) => {
      if (row.kind === "single") {
        if (!row.is_attribute) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: strings(t).thisIsARequiredField,
            path: ["is_attribute"],
          });
        }
        return;
      }
      // range: display name is always required, but
      // only ≥1 of Is/Min/Max needs to be filled, not all three.
      if (!row.display_name.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: strings(t).thisIsARequiredField,
          path: ["display_name"],
        });
      }
      if (!row.is_attribute && !row.min_attribute && !row.max_attribute) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("At least one of Is/Min/Max attribute is required"),
          path: ["min_attribute"],
        });
      }
    });

/**
 * Client-side pre-check ahead of the backend's authoritative 400:
 * no attribute may be used in more than one of {Key Attribute} ∪
 * {every filled quantitative slot across every card} ∪ {every Other Filter
 * attribute} within one collection instance.
 */
const ConfigureAdvancedFilterSchemaFn = (t: TFunction) =>
  z
    .object({
      collection: zodNullableOption,
      key_attribute: zodNullableOption,
      quantitative_filters: z.array(quantitativeFilterRowSchema(t)).default([]),
      other_filters: z
        .array(z.object({ attribute: zodNullableOption }))
        .default([]),
    })
    .superRefine(
      (
        { collection, key_attribute, quantitative_filters, other_filters },
        ctx
      ) => {
        if (!collection) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: strings(t).thisIsARequiredField,
            path: ["collection"],
          });
        }
        if (!key_attribute) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: strings(t).thisIsARequiredField,
            path: ["key_attribute"],
          });
        }
        other_filters.forEach((row, index) => {
          if (!row.attribute) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: strings(t).thisIsARequiredField,
              path: ["other_filters", index, "attribute"],
            });
          }
        });

        const entries: { id: string; path: (string | number)[] }[] = [];
        if (key_attribute?.value) {
          entries.push({ id: key_attribute.value, path: ["key_attribute"] });
        }
        quantitative_filters.forEach((row, index) => {
          (["is_attribute", "min_attribute", "max_attribute"] as const).forEach(
            (slot) => {
              const id = row[slot]?.value;
              if (id) {
                entries.push({
                  id,
                  path: ["quantitative_filters", index, slot],
                });
              }
            }
          );
        });
        other_filters.forEach((row, index) => {
          if (row.attribute?.value) {
            entries.push({
              id: row.attribute.value,
              path: ["other_filters", index, "attribute"],
            });
          }
        });

        const duplicateIdxs = findNonEmptyDuplicateIndexes(
          entries.map(({ id }) => id)
        );
        duplicateIdxs.forEach((idx) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("Attribute already used elsewhere in this filter"),
            path: entries[idx].path,
          });
        });
      }
    );

function attributeToOption(attribute: AttributeSchema): OptionType {
  return {
    value: attribute.id,
    label: attribute.display_name || attribute.name,
  };
}

/** Every attribute id already selected anywhere in the form, except the one field currently being rendered (so its own current selection stays choosable). */
function usedAttributeIds(
  values: FormValues,
  exclude:
    | { field: "key_attribute" }
    | { field: "quantitative"; index: number; slot: QuantitativeFilterSlot }
    | { field: "other"; index: number }
): Set<string> {
  const ids = new Set<string>();
  if (!(exclude.field === "key_attribute") && values.key_attribute?.value) {
    ids.add(values.key_attribute.value);
  }
  values.quantitative_filters.forEach((row, index) => {
    (["is_attribute", "min_attribute", "max_attribute"] as const).forEach(
      (slot) => {
        if (
          exclude.field === "quantitative" &&
          exclude.index === index &&
          exclude.slot === slot
        ) {
          return;
        }
        const id = row[slot]?.value;
        if (id) ids.add(id);
      }
    );
  });
  values.other_filters.forEach((row, index) => {
    if (exclude.field === "other" && exclude.index === index) return;
    if (row.attribute?.value) ids.add(row.attribute.value);
  });
  return ids;
}

function buildAttributeOptions(
  columns: AttributeSchema[],
  allowedInputTypes: AttributeInput[],
  excludeIds: Set<string>
): OptionType[] {
  return columns
    .filter(
      (col) => !!col.input_type && allowedInputTypes.includes(col.input_type)
    )
    .filter((col) => !excludeIds.has(col.id))
    .map(attributeToOption);
}

export const ConfigureAdvancedFilterForm = ({
  existingInstance,
  handleFormSubmit,
}: {
  existingInstance?: AdvancedFilterCollectionInstance;
  handleFormSubmit: () => void;
}) => {
  const { t } = useTranslation();
  const { notifyError, notifySuccess } = useContext(Notifications);
  const { storefront_id, tenant_id } = useStoreState();
  const [submitting, setSubmitting] = useState(false);

  const methodsOfUseForm = useFormWrapper<FormValues>({
    resolver: zodResolver(ConfigureAdvancedFilterSchemaFn(t)),
    defaultValues: {
      collection: existingInstance
        ? {
            value: existingInstance.collection_id,
            label: existingInstance.collection_name,
          }
        : null,
      key_attribute: existingInstance
        ? {
            value: existingInstance.key_attribute_id,
            label: existingInstance.key_attribute_display_name,
          }
        : null,
      quantitative_filters: [],
      other_filters: [],
    },
    reValidateMode: "onChange",
  });
  const {
    handleSubmit,
    control,
    formState,
    errors,
    watch,
    register,
    reset,
    setValue,
  } = methodsOfUseForm;

  const {
    fields: quantitativeFields,
    append: appendQuantitative,
    remove: removeQuantitative,
  } = useFieldArray({ control, name: "quantitative_filters" });
  const {
    fields: otherFields,
    append: appendOther,
    remove: removeOther,
  } = useFieldArray({ control, name: "other_filters" });

  const watchedValues = watch();
  const values: FormValues = {
    ...watchedValues,
    quantitative_filters: watchedValues.quantitative_filters ?? [],
    other_filters: watchedValues.other_filters ?? [],
  };
  const collectionId = values.collection?.value;

  const { data: collectionDetail } = useSWR<CollectionSchema>(
    collectionId
      ? endpoints.v2_tenants_id_pim_collections_id(tenant_id, collectionId)
      : null
  );
  const columns = useMemo(
    () => collectionDetail?.columns ?? [],
    [collectionDetail]
  );

  const previousCollectionIdRef = useRef(collectionId);
  useEffect(() => {
    if (previousCollectionIdRef.current !== collectionId) {
      setValue("key_attribute", null);
      removeQuantitative();
      removeOther();
    }
    previousCollectionIdRef.current = collectionId;
  }, [collectionId, setValue, removeQuantitative, removeOther]);

  useEffect(() => {
    if (!existingInstance || !collectionDetail) return;
    const findLabel = (id: string) =>
      collectionDetail.columns.find((col) => col.id === id)?.display_name ??
      collectionDetail.columns.find((col) => col.id === id)?.name ??
      id;
    reset({
      collection: {
        value: existingInstance.collection_id,
        label: existingInstance.collection_name,
      },
      key_attribute: {
        value: existingInstance.key_attribute_id,
        label: existingInstance.key_attribute_display_name,
      },
      quantitative_filters: existingInstance.quantitative_filters.map((qf) => {
        if (qf.kind === "single") {
          return {
            kind: "single" as const,
            display_name: "",
            is_attribute: {
              value: qf.is_attribute_id,
              label: findLabel(qf.is_attribute_id),
            },
            min_attribute: null,
            max_attribute: null,
          };
        }
        return {
          kind: "range" as const,
          display_name: qf.display_name,
          is_attribute: qf.is_attribute_id
            ? {
                value: qf.is_attribute_id,
                label: findLabel(qf.is_attribute_id),
              }
            : null,
          min_attribute: qf.min_attribute_id
            ? {
                value: qf.min_attribute_id,
                label: findLabel(qf.min_attribute_id),
              }
            : null,
          max_attribute: qf.max_attribute_id
            ? {
                value: qf.max_attribute_id,
                label: findLabel(qf.max_attribute_id),
              }
            : null,
        };
      }),
      other_filters: existingInstance.other_filters.map((of) => ({
        attribute: {
          value: of.attribute_id,
          label: findLabel(of.attribute_id),
        },
      })),
    });
    // Only re-run when the collection detail this instance depends on loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionDetail]);

  const buildQuantitativeFilterInput = (
    row: QuantitativeFilterFormRow
  ): AdvancedFilterQuantitativeFilterInput | null => {
    if (row.kind === "single") {
      if (!row.is_attribute) return null;
      return { kind: "single", is_attribute_id: row.is_attribute.value };
    }
    if (!row.is_attribute && !row.min_attribute && !row.max_attribute) {
      return null;
    }
    return {
      kind: "range",
      display_name: row.display_name.trim(),
      is_attribute_id: row.is_attribute?.value ?? null,
      min_attribute_id: row.min_attribute?.value ?? null,
      max_attribute_id: row.max_attribute?.value ?? null,
    };
  };

  const onSubmit = async (formValues: FormValues) => {
    if (!formValues.collection || !formValues.key_attribute) return;

    const body: AdvancedFilterCollectionInstanceInput = {
      collection_id: formValues.collection.value,
      key_attribute_id: formValues.key_attribute.value,
      // PATCH is full-replace — always submit the complete
      // current arrays, never a diff against what was originally fetched.
      quantitative_filters: formValues.quantitative_filters
        .map(buildQuantitativeFilterInput)
        .filter(
          (qf): qf is AdvancedFilterQuantitativeFilterInput => qf !== null
        ),
      other_filters: formValues.other_filters
        .filter((row) => !!row.attribute?.value)
        .map(
          (row): AdvancedFilterOtherFilterInput => ({
            attribute_id: row.attribute?.value ?? "",
          })
        ),
    };

    setSubmitting(true);
    try {
      if (existingInstance) {
        await Axios.patch(
          endpoints.v2_storefronts_id_advancedFilters_collectionInstances_id(
            storefront_id,
            existingInstance.id
          ),
          body
        );
      } else {
        await Axios.post(
          endpoints.v2_storefronts_id_advancedFilters_collectionInstances(
            storefront_id
          ),
          body
        );
      }
      notifySuccess(t("Your changes have been saved successfully"));
      handleFormSubmit();
    } catch (error) {
      const backendMessage = (error as AxiosError<{ message?: string }>)
        ?.response?.data?.message;
      notifyError(
        backendMessage ?? t("Something went wrong, please try again"),
        { error }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const keyAttributeOptions = buildAttributeOptions(
    columns,
    KEY_ATTRIBUTE_INPUT_TYPES,
    usedAttributeIds(values, { field: "key_attribute" })
  );

  return (
    <>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Controller
          as={SearchSelectInfiniteScroll}
          control={control}
          name="collection"
          placeholder={t("Select Collection")}
          baseUrl={endpoints.v2_tenants_id_pim_collections(tenant_id)}
          getOptions={(collections: CollectionSchema[]) =>
            collections.map((collection) => ({
              value: collection.id,
              label: collection.display_name || collection.name,
            }))
          }
          errors={errors}
          disabled={!!existingInstance}
          formState={formState}
          defaultOptions
        />

        <Controller
          as={SelectBoxV2}
          control={control}
          name="key_attribute"
          placeholder={
            collectionId
              ? t("Select Key Attribute")
              : t("Select a collection first")
          }
          options={keyAttributeOptions}
          disabled={!!existingInstance || !collectionId}
          errors={errors}
          formState={formState}
        />

        <FilterSection>
          <TextMdBold style={{ display: "block" }}>
            {t("Quantitative Filters")}
          </TextMdBold>
          {quantitativeFields.map((field, index) => (
            <QuantitativeFilterCard
              key={field.id}
              index={index}
              control={control}
              register={register}
              errors={{
                [`quantitative_filters[${index}].display_name`]:
                  errors?.quantitative_filters?.[index]?.display_name ??
                  undefined,
                [`quantitative_filters[${index}].is_attribute`]:
                  errors?.quantitative_filters?.[index]?.is_attribute ??
                  undefined,
                [`quantitative_filters[${index}].min_attribute`]:
                  errors?.quantitative_filters?.[index]?.min_attribute ??
                  undefined,
                [`quantitative_filters[${index}].max_attribute`]:
                  errors?.quantitative_filters?.[index]?.max_attribute ??
                  undefined,
              }}
              formState={formState}
              kind={values.quantitative_filters[index]?.kind ?? "single"}
              defaultValues={{ ...emptyQuantitativeFilterRow, ...field }}
              onKindChange={(kind) => {
                setValue(`quantitative_filters[${index}].kind`, kind);
                setValue(`quantitative_filters[${index}].display_name`, "");
                setValue(`quantitative_filters[${index}].is_attribute`, null);
                setValue(`quantitative_filters[${index}].min_attribute`, null);
                setValue(`quantitative_filters[${index}].max_attribute`, null);
              }}
              buildOptionsForSlot={(slot) =>
                buildAttributeOptions(
                  columns,
                  QUANTITATIVE_INPUT_TYPES,
                  usedAttributeIds(values, {
                    field: "quantitative",
                    index,
                    slot,
                  })
                )
              }
              disabled={!collectionId}
              onRemove={() => removeQuantitative(index)}
            />
          ))}
          <SecondaryButtonWithPlusIcon
            type="button"
            disabled={!collectionId}
            onClick={() => appendQuantitative(emptyQuantitativeFilterRow)}
          >
            {t("Add Filter")}
          </SecondaryButtonWithPlusIcon>
        </FilterSection>

        <FilterSection>
          <TextMdBold style={{ display: "block" }}>
            {t("Other Filters")}
          </TextMdBold>
          {otherFields.map((field, index) => (
            <SelectWithDeleteButton
              style={{
                marginBottom: errors?.other_filters?.[index]?.attribute
                  ? spacingTokens.spacing_lg
                  : spacingTokens.spacing_md,
              }}
              key={field.id}
            >
              <Controller
                as={SelectBoxV2}
                control={control}
                name={`other_filters[${index}].attribute`}
                placeholder={t("Select Attribute")}
                options={buildAttributeOptions(
                  columns,
                  OTHER_FILTER_INPUT_TYPES,
                  usedAttributeIds(values, { field: "other", index })
                )}
                disabled={!collectionId}
                errors={{
                  [`other_filters[${index}].attribute`]:
                    errors?.other_filters?.[index]?.attribute ?? undefined,
                }}
                formState={formState}
              />
              <OutlineIconButtonMedium
                testid={`delete-other-filter-${index}`}
                onClick={() => removeOther(index)}
                type="button"
              >
                <DeleteIcon />
              </OutlineIconButtonMedium>
            </SelectWithDeleteButton>
          ))}
          <SecondaryButtonWithPlusIcon
            type="button"
            disabled={!collectionId}
            onClick={() => appendOther({ attribute: null })}
          >
            {t("Add Filter")}
          </SecondaryButtonWithPlusIcon>
        </FilterSection>

        <ConfirmOrCancelButtonContainer
          confirmText={t("Save")}
          isConfirmLoading={submitting}
          onConfirm={handleSubmit(onSubmit)}
          onCancel={handleFormSubmit}
        />
      </Form>
    </>
  );
};
